import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import {
  buildQuickViewProductData,
  canAddSizeVariant,
  dispatchCartUpdate,
  fetchAndCacheColorVariantProducts,
  isCartAddFailure,
  isInventoryLimitError,
  isSizeVariantPurchasable,
} from '@theme/search-drawer-product-card';

/**
 * Escapes HTML special characters.
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Product quick view drawer opened from search drawer product cards.
 * Desktop: right drawer. Mobile: left drawer with progressbar gallery.
 *
 * @typedef {object} Refs
 * @property {HTMLDivElement} overlay - Full-screen overlay wrapper.
 * @property {HTMLDivElement} backdrop - Clickable backdrop.
 * @property {HTMLDivElement} panel - Drawer panel.
 * @property {HTMLButtonElement} closeButton - Close button.
 * @property {HTMLHeadingElement} heading - Product title.
 * @property {HTMLDivElement} price - Product price container.
 * @property {HTMLHeadingElement} mobileHeading - Mobile-only product title below gallery.
 * @property {HTMLDivElement} mobilePrice - Mobile-only product price below gallery.
 * @property {HTMLDivElement} gallery - Product image gallery.
 * @property {HTMLDivElement} galleryWrapper - Swiper slides wrapper.
 * @property {HTMLDivElement} colorsSection - Colour option section.
 * @property {HTMLDivElement} colors - Colour swatches container.
 * @property {HTMLDivElement} sizesSection - Size option section.
 * @property {HTMLParagraphElement} sizeLabel - Size option label.
 * @property {HTMLDivElement} sizes - Size buttons container.
 * @property {HTMLParagraphElement} errorText - Add to cart error message.
 * @property {HTMLButtonElement} addToCart - Add to cart button.
 *
 * @extends Component<Refs>
 */
class NeoQuickViewModal extends Component {
  #previousScrollY = 0;
  /** @type {HTMLElement | null} */
  #lastTrigger = null;
  /** @type {string|null} */
  #activeProductId = null;
  /** @type {string|null} */
  #selectedVariantId = null;
  /** @type {import('swiper').default | null} */
  #gallerySwiper = null;
  /** @type {number} */
  #colorSwitchToken = 0;
  /** @type {Set<string>} */
  #inventoryBlockedVariantIds = new Set();

  connectedCallback() {
    super.connectedCallback();

    requestIdleCallback(() => {
      this.#bindEvents();
    });
  }

  disconnectedCallback() {
    this.#unbindEvents();
    this.#destroyGallerySwiper();
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#handleKeyDown);
  }

  #bindEvents() {
    this.refs.closeButton?.addEventListener('click', this.closeModal);
    this.refs.backdrop?.addEventListener('click', this.#handleBackdropClick);
    this.refs.colors?.addEventListener('click', this.#handleColorClick);
    this.refs.sizes?.addEventListener('click', this.#handleSizeClick);
    this.refs.addToCart?.addEventListener('click', this.#handleAddToCart);
    document.addEventListener('search-drawer-product-card:cart', this.#handleCartIconClick);
    document.addEventListener('cart:update', this.#onCartUpdate);
  }

  #unbindEvents() {
    this.refs.closeButton?.removeEventListener('click', this.closeModal);
    this.refs.backdrop?.removeEventListener('click', this.#handleBackdropClick);
    this.refs.colors?.removeEventListener('click', this.#handleColorClick);
    this.refs.sizes?.removeEventListener('click', this.#handleSizeClick);
    this.refs.addToCart?.removeEventListener('click', this.#handleAddToCart);
    document.removeEventListener('search-drawer-product-card:cart', this.#handleCartIconClick);
    document.removeEventListener('cart:update', this.#onCartUpdate);
  }

  /**
   * Closes other open modals/drawers before opening quick view.
   */
  async #closeOtherModals() {
    const searchDrawer = /** @type {HTMLElement & { closeModal?: () => Promise<void> } | null} */ (
      document.getElementById('search-drawer-modal')
    );

    if (typeof searchDrawer?.closeModal === 'function') {
      const searchOverlay = searchDrawer.querySelector('.search-drawer-modal__overlay');
      if (searchOverlay?.hasAttribute('data-open')) {
        await searchDrawer.closeModal();
      }
    }

    const signupModal = /** @type {HTMLElement & { closeModal?: () => void } | null} */ (
      document.getElementById('neo-signup-modal')
    );

    if (typeof signupModal?.closeModal === 'function') {
      const signupOverlay = signupModal.querySelector('.neo-signup-modal__overlay');
      if (signupOverlay && !signupOverlay.hasAttribute('hidden')) {
        signupModal.closeModal();
      }
    }

    const quickAddDialog = /** @type {HTMLElement & { closeDialog?: () => void } | null} */ (
      document.getElementById('quick-add-dialog')
    );

    if (typeof quickAddDialog?.closeDialog === 'function') {
      const dialog = quickAddDialog.querySelector('dialog');
      if (dialog?.open) {
        quickAddDialog.closeDialog();
      }
    }
  }

  /**
   * Opens the quick view drawer for a product id from the search modal store.
   *
   * @param {string|number} productId - Product id from the trigger button.
   * @param {HTMLElement} [trigger] - Element that opened the drawer.
   */
  openModal = async (productId, trigger) => {
    const { overlay } = this.refs;

    if (!overlay || overlay.hasAttribute('data-open')) return;

    const viewData = buildQuickViewProductData(productId);
    if (!viewData) return;

    await this.#closeOtherModals();

    this.#lastTrigger = trigger ?? null;
    this.#activeProductId = viewData.productId;
    this.#selectedVariantId = null;
    this.#renderProduct(viewData);
    this.#previousScrollY = window.scrollY;
    overlay.removeAttribute('data-closing');
    overlay.setAttribute('data-open', '');
    document.documentElement.setAttribute('scroll-lock', '');
    document.addEventListener('keydown', this.#handleKeyDown);
    this.refs.closeButton?.focus();
  };

  /**
   * Closes the quick view drawer.
   */
  closeModal = async () => {
    const { overlay, panel } = this.refs;

    if (!overlay?.hasAttribute('data-open') || overlay.hasAttribute('data-closing')) return;

    overlay.setAttribute('data-closing', '');

    if (panel) {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await onAnimationEnd(panel, undefined, { subtree: false });
    }

    overlay.removeAttribute('data-open');
    overlay.removeAttribute('data-closing');
    document.documentElement.removeAttribute('scroll-lock');
    document.removeEventListener('keydown', this.#handleKeyDown);
    this.#destroyGallerySwiper();
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    this.#lastTrigger?.focus();
    this.#lastTrigger = null;
    this.#activeProductId = null;
    this.#selectedVariantId = null;
  };

  /**
   * @param {CustomEvent} event
   */
  #handleCartIconClick = (event) => {
    const trigger = event.detail?.trigger instanceof HTMLElement ? event.detail.trigger : null;
    const productId = trigger?.getAttribute('data-product-id') || event.detail?.productId;

    if (!productId) return;

    this.openModal(productId, trigger);
  };

  /**
   * Closes the drawer when the backdrop is clicked.
   *
   * @param {MouseEvent} event
   */
  #handleBackdropClick = (event) => {
    if (event.target !== this.refs.backdrop) return;
    this.closeModal();
  };

  /**
   * Closes the drawer on Escape key press.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    const { overlay } = this.refs;

    if (event.key !== 'Escape' || !overlay?.hasAttribute('data-open')) return;

    event.preventDefault();
    this.closeModal();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleColorClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-action="select-color"]');
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();

    const variantProductId = button.getAttribute('data-color-variant-id');
    if (!variantProductId || variantProductId === this.#activeProductId) return;

    void this.#switchColorVariant(variantProductId);
  };

  /**
   * Loads the selected colour product (from cache, then GraphQL if needed) and
   * re-renders sizes with that colour's inventory.
   * @param {string} variantProductId
   */
  async #switchColorVariant(variantProductId) {
    const token = ++this.#colorSwitchToken;
    const cachedView = buildQuickViewProductData(variantProductId);

    if (cachedView) {
      this.#activeProductId = cachedView.productId;
      this.#selectedVariantId = null;
      this.#renderProduct(cachedView);
    }

    await fetchAndCacheColorVariantProducts([variantProductId]);
    if (token !== this.#colorSwitchToken) return;

    const viewData = buildQuickViewProductData(variantProductId);
    if (!viewData) return;

    this.#activeProductId = viewData.productId;
    this.#selectedVariantId = null;
    this.#renderProduct(viewData);
  }

  #onCartUpdate = () => {
    this.#inventoryBlockedVariantIds.clear();
    if (!this.refs.overlay?.hasAttribute('data-open')) return;
    this.#syncInventoryState();
  };

  /**
   * @param {object|null|undefined} variant
   * @returns {boolean}
   */
  #isSizeSelectable(variant) {
    if (!variant) return false;
    if (this.#inventoryBlockedVariantIds.has(String(variant.id))) return false;
    return isSizeVariantPurchasable(variant);
  }

  /**
   * Re-reads the open product from cache and refreshes size / ATC availability.
   */
  #syncInventoryState() {
    if (!this.#activeProductId) return;

    const viewData = buildQuickViewProductData(this.#activeProductId);
    if (!viewData) return;

    this.#renderSizes(this.refs.sizesSection, this.refs.sizes, viewData.sizeVariants);
  }

  /**
   * @returns {object|null}
   */
  #getSelectedVariant() {
    if (!this.#activeProductId || !this.#selectedVariantId) return null;

    const viewData = buildQuickViewProductData(this.#activeProductId);
    const variants = Array.isArray(viewData?.sizeVariants) ? viewData.sizeVariants : [];
    return variants.find((item) => String(item?.id) === String(this.#selectedVariantId)) || null;
  }

  /**
   * @param {MouseEvent} event
   */
  #handleSizeClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-action="select-size"]');
    if (!(button instanceof HTMLButtonElement) || button.hasAttribute('disabled')) return;

    event.preventDefault();

    this.refs.sizes?.querySelectorAll('[data-action="select-size"]').forEach((sizeButton) => {
      sizeButton.removeAttribute('data-selected');
    });

    button.setAttribute('data-selected', '');
    this.#selectedVariantId = button.getAttribute('data-variant-id');
    this.#updateSizeLabel(button.textContent?.trim() || '');
    this.#clearErrorMessage();
    this.refs.addToCart?.toggleAttribute('disabled', !this.#selectedVariantId);
  };

  /**
   * Updates the size option label with the selected size value.
   * @param {string} [selectedSize]
   */
  #updateSizeLabel(selectedSize = '') {
    const { sizeLabel } = this.refs;
    if (!(sizeLabel instanceof HTMLElement)) return;

    sizeLabel.textContent = selectedSize ? `Size: ${selectedSize}` : 'Size:';
  }

  /**
   * Shows an add-to-cart error under the footer button.
   * @param {string} message
   */
  #setErrorMessage(message) {
    const { errorText } = this.refs;
    if (!(errorText instanceof HTMLElement)) return;

    errorText.textContent = message || 'Add to cart failed';
    errorText.removeAttribute('hidden');
  }

  /**
   * Clears the add-to-cart error message.
   */
  #clearErrorMessage() {
    const { errorText } = this.refs;
    if (!(errorText instanceof HTMLElement)) return;

    errorText.textContent = '';
    errorText.setAttribute('hidden', '');
  }

  /**
   * @returns {{ addToCart: string, outOfStock: string }}
   */
  #getButtonLabels() {
    return {
      addToCart: this.dataset.addToCartLabel || 'Add to cart',
      outOfStock: this.dataset.outOfStockLabel || 'Out of stock',
    };
  }

  /**
   * Updates footer button label and disabled state from size availability.
   * @param {object[]} sizeVariants
   */
  #updateAddToCartButtonState(sizeVariants) {
    const addToCart = this.refs.addToCart;
    if (!(addToCart instanceof HTMLButtonElement)) return;

    const labels = this.#getButtonLabels();
    const safeVariants = Array.isArray(sizeVariants) ? sizeVariants : [];
    const allSizesSoldOut =
      safeVariants.length > 0 && safeVariants.every((variant) => !this.#isSizeSelectable(variant));
    const readyState = addToCart.querySelector('.btn-state-ready');

    addToCart.toggleAttribute('data-loading', false);
    addToCart.toggleAttribute('data-success', false);
    addToCart.removeAttribute('aria-busy');

    if (allSizesSoldOut) {
      this.#selectedVariantId = null;
      if (readyState instanceof HTMLElement) {
        readyState.textContent = labels.outOfStock;
      }
      addToCart.setAttribute('disabled', '');
      addToCart.setAttribute('data-sold-out', '');
      return;
    }

    addToCart.removeAttribute('data-sold-out');
    if (readyState instanceof HTMLElement) {
      readyState.textContent = labels.addToCart;
    }
    addToCart.toggleAttribute('disabled', !this.#selectedVariantId);
  }

  #handleAddToCart = () => {
    const addToCartButton = this.refs.addToCart;
    if (!this.#selectedVariantId || !(addToCartButton instanceof HTMLButtonElement)) return;
    if (addToCartButton.hasAttribute('data-sold-out')) return;
    if (addToCartButton.hasAttribute('data-loading')) return;

    this.#clearErrorMessage();
    const variant = this.#getSelectedVariant();
    const availability = canAddSizeVariant(variant, 1);

    if (!variant || !availability.ok) {
      this.#setErrorMessage(this.#getButtonLabels().outOfStock);
      this.#syncInventoryState();
      return;
    }

    this.#addVariantToCart(this.#selectedVariantId, addToCartButton);
  };

  /**
   * @param {object} viewData
   */
  #renderProduct(viewData) {
    const { heading, price, mobileHeading, mobilePrice, gallery, colorsSection, colors, sizesSection, sizes } =
      this.refs;

    this.#clearErrorMessage();

    if (heading) {
      heading.textContent = viewData.title;
    }

    if (mobileHeading instanceof HTMLElement) {
      mobileHeading.textContent = viewData.title;
    }

    const compareHtml = viewData.compareAt
      ? `<span class="neo-quick-view-modal__price-compare">${viewData.compareAt}</span>`
      : '';
    const priceHtml = `<span class="neo-quick-view-modal__price-current">${viewData.price}</span>${compareHtml}`;

    if (price) {
      price.innerHTML = priceHtml;
    }

    if (mobilePrice instanceof HTMLElement) {
      mobilePrice.innerHTML = priceHtml;
    }

    this.#renderGallery(gallery, viewData.images, viewData.imageAlt);
    this.#renderColorSwatches(colorsSection, colors, viewData.colorVariants, viewData.productId);
    this.#renderSizes(sizesSection, sizes, viewData.sizeVariants);
    this.#updateAddToCartButtonState(viewData.sizeVariants);
  }

  /**
   * Destroys the gallery Swiper instance if present.
   */
  #destroyGallerySwiper() {
    const swiperRoot = this.refs.gallery?.querySelector('[data-neo-quick-view-gallery]');
    const swiperElement = /** @type {HTMLElement & { __swiper?: import('swiper').default }} */ (swiperRoot);

    if (!swiperElement?.__swiper) return;

    swiperElement.__swiper.destroy(true, true);
    swiperElement.__swiper = null;
    this.#gallerySwiper = null;
  }

  /**
   * Waits until Swiper is available on window.
   * @returns {Promise<typeof import('swiper').default | undefined>}
   */
  async #waitForSwiper() {
    if (window.Swiper) return window.Swiper;

    return new Promise((resolve) => {
      const check = () => {
        if (window.Swiper) {
          resolve(window.Swiper);
          return;
        }
        requestAnimationFrame(check);
      };
      check();
    });
  }

  /**
   * Initializes Swiper on the product image gallery.
   * @returns {Promise<void>}
   */
  async #initGallerySwiper() {
    const SwiperConstructor = await this.#waitForSwiper();
    if (!SwiperConstructor) return;

    const gallery = this.refs.gallery;
    const swiperRoot = gallery?.querySelector('[data-neo-quick-view-gallery]');
    const swiperElement = /** @type {HTMLElement & { __swiper?: import('swiper').default }} */ (swiperRoot);

    if (!swiperElement || swiperElement.__swiper) return;

    const slideCount = swiperElement.querySelectorAll('.swiper-slide').length;
    if (!slideCount) return;

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    /** @type {Record<string, unknown>} */
    const swiperOptions = {
      slidesPerView: isMobile ? 1 : 2.5,
      spaceBetween: 0,
      watchOverflow: true,
    };

    if (isMobile && slideCount > 1) {
      const paginationEl = gallery?.querySelector('[data-neo-quick-view-pagination]');
      if (paginationEl instanceof HTMLElement) {
        swiperOptions.pagination = {
          el: paginationEl,
          type: 'progressbar',
        };
      }
    }

    this.#gallerySwiper = new SwiperConstructor(swiperElement, swiperOptions);
    swiperElement.__swiper = this.#gallerySwiper;
  }

  /**
   * @param {HTMLDivElement|undefined} gallery
   * @param {string[]} images
   * @param {string} imageAlt
   */
  #renderGallery(gallery, images, imageAlt) {
    if (!(gallery instanceof HTMLElement)) return;

    this.#destroyGallerySwiper();

    const wrapper = this.refs.galleryWrapper;
    const safeImages = Array.isArray(images) ? images.filter(Boolean).map(String) : [];

    if (!(wrapper instanceof HTMLElement) || !safeImages.length) {
      if (wrapper instanceof HTMLElement) {
        wrapper.innerHTML = '';
      }
      gallery.setAttribute('hidden', '');
      gallery.removeAttribute('data-single-image');
      return;
    }

    gallery.removeAttribute('hidden');
    gallery.toggleAttribute('data-single-image', safeImages.length < 2);

    wrapper.innerHTML = safeImages
      .map((imageSrc, index) => {
        const loading = index === 0 ? 'eager' : 'lazy';

        return `<div class="swiper-slide">
          <img
            class="neo-quick-view-modal__gallery-image"
            src="${escapeHtml(imageSrc)}"
            alt="${escapeHtml(imageAlt)}"
            loading="${loading}"
            decoding="async"
            width="683"
            height="1024"
          >
        </div>`;
      })
      .join('');

    this.#initGallerySwiper();
  }

  /**
   * @param {HTMLDivElement|undefined} colorsSection
   * @param {HTMLDivElement|undefined} colors
   * @param {object[]} colorVariants
   * @param {string} activeProductId
   */
  #renderColorSwatches(colorsSection, colors, colorVariants, activeProductId) {
    if (!(colorsSection instanceof HTMLElement) || !(colors instanceof HTMLElement)) return;

    const safeVariants = Array.isArray(colorVariants) ? colorVariants.filter((variant) => variant?.id) : [];

    if (!safeVariants.length) {
      colors.innerHTML = '';
      colorsSection.setAttribute('hidden', '');
      return;
    }

    colorsSection.removeAttribute('hidden');
    colors.innerHTML = safeVariants
      .map((variant) => {
        const isActive = String(variant.id) === String(activeProductId);
        const swatchColor = String(variant.swatchColor || '');
        const loadedClass = swatchColor ? ' neo-quick-view-modal__color-swatch--loaded' : '';
        const whiteClass = variant.isWhite ? ' neo-quick-view-modal__color-swatch--white' : '';
        const activeClass = isActive ? ' neo-quick-view-modal__color-swatch--active' : '';
        const swatchStyle = swatchColor ? ` style="--swatch-color: ${escapeHtml(swatchColor)}"` : '';

        return `<button
          type="button"
          class="neo-quick-view-modal__color-swatch${loadedClass}${whiteClass}${activeClass}"
          data-action="select-color"
          data-color-variant-id="${escapeHtml(String(variant.id))}"
          aria-label="Colour variant"
          ${isActive ? 'aria-current="true"' : ''}${swatchStyle}
        ></button>`;
      })
      .join('');
  }

  /**
   * @param {HTMLDivElement|undefined} sizesSection
   * @param {HTMLDivElement|undefined} sizes
   * @param {object[]} sizeVariants
   */
  #renderSizes(sizesSection, sizes, sizeVariants) {
    if (!(sizesSection instanceof HTMLElement) || !(sizes instanceof HTMLElement)) return;

    const safeVariants = Array.isArray(sizeVariants) ? sizeVariants : [];

    if (!safeVariants.length) {
      sizes.innerHTML = '';
      sizesSection.setAttribute('hidden', '');
      return;
    }

    sizesSection.removeAttribute('hidden');

    if (this.#selectedVariantId) {
      const selected = safeVariants.find((item) => String(item?.id) === String(this.#selectedVariantId));
      if (!this.#isSizeSelectable(selected)) {
        this.#selectedVariantId = null;
      }
    }

    sizes.innerHTML = safeVariants
      .map((variant) => {
        const sizeLabel = String(variant?.title || '');
        const isAvailable = this.#isSizeSelectable(variant);
        const variantId = variant?.id;
        const isSelected = String(variantId) === String(this.#selectedVariantId);
        const isDefaultTitle = sizeLabel.toLowerCase().includes('default title');
        const sizeClasses = [
          'neo-quick-view-modal__size',
          isAvailable ? '' : 'neo-quick-view-modal__size--unavailable',
          isDefaultTitle ? 'neo-quick-view-modal__size--default-title' : '',
        ]
          .filter(Boolean)
          .join(' ');

        return `<button
          type="button"
          class="${sizeClasses}"
          data-action="select-size"
          data-variant-id="${escapeHtml(String(variantId || ''))}"
          ${isAvailable ? '' : 'disabled'}
          ${isSelected ? 'data-selected' : ''}
          aria-label="Select size ${escapeHtml(sizeLabel)}"
        >${escapeHtml(sizeLabel)}</button>`;
      })
      .join('');

    const selectedVariant = safeVariants.find(
      (variant) => String(variant?.id) === String(this.#selectedVariantId)
    );
    this.#updateSizeLabel(selectedVariant ? String(selectedVariant.title || '') : '');
    this.#updateAddToCartButtonState(safeVariants);
  }

  /**
   * @param {string|null} variantId
   * @param {HTMLButtonElement} button
   */
  async #addVariantToCart(variantId, button) {
    if (!variantId) return;
    if (button.hasAttribute('data-loading') || button.hasAttribute('data-success')) return;

    const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';

    button.toggleAttribute('data-loading', true);
    button.toggleAttribute('data-success', false);
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
        }),
      });

      const result = await response.json();

      if (isCartAddFailure(response, result)) {
        const status = Number(result?.status);
        if (isInventoryLimitError(result) || status === 422 || response.status === 422) {
          this.#inventoryBlockedVariantIds.add(String(variantId));
          this.#selectedVariantId = null;
          this.#syncInventoryState();
        }
        throw new Error(result?.description || result?.message || 'Add to cart failed');
      }

      button.toggleAttribute('data-loading', false);
      button.toggleAttribute('data-success', true);
      button.removeAttribute('aria-busy');
      this.#clearErrorMessage();

      const addedProductId = this.#activeProductId;
      this.closeModal();
      dispatchCartUpdate('neo-quick-view-modal', addedProductId);
    } catch (error) {
      button.toggleAttribute('data-loading', false);
      button.removeAttribute('aria-busy');
      this.#setErrorMessage(error instanceof Error ? error.message : 'Add to cart failed');
      console.error('[neo-quick-view-modal] add to cart failed', error);
    }
  }
}

if (!customElements.get('neo-quick-view-modal')) {
  customElements.define('neo-quick-view-modal', NeoQuickViewModal);
}
